/**
 * S6 — Run catalog and start-run flow (`/ai-studio/runs`).
 *
 * Global list of all agent runs. Start from a deployed instance (REQ-AIS-114).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { RunTable, StartRunForm } from '@/components/ai-studio/runs';
import { createRun, listInstances, listRuns } from '@/services/aiStudioAgentService';
import { STUDIO_RUN_STATUSES } from '@/types/aiStudioAgent';
import type {
  AiStudioAgentInstance,
  AiStudioRun,
  CreateRunRequest,
  StudioRunStatus,
} from '@/types/aiStudioAgent';

const FIELD_CLASS =
  'rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-3 py-2 text-base text-[var(--color-surface-text)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]';
const LABEL_CLASS = 'mb-1 block text-base font-medium text-[var(--color-surface-text)]';

const AIStudioRunCatalogPage: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [runs, setRuns] = useState<AiStudioRun[]>([]);
  const [instances, setInstances] = useState<AiStudioAgentInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [instanceFilter, setInstanceFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StudioRunStatus | ''>('');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [errorDetail, setErrorDetail] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([listRuns(), listInstances()])
      .then(([runRows, instanceRows]) => {
        if (cancelled) return;
        setRuns(runRows);
        setInstances(instanceRows);
      })
      .catch(() => {
        if (!cancelled) setErrorKey('aiStudio.runs.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runnableInstances = useMemo(
    () => instances.filter((row) => row.status === 'active' && row.setupConfigured !== false),
    [instances]
  );

  const visibleRuns = useMemo(() => {
    const term = search.trim().toLowerCase();
    return runs.filter((run) => {
      const matchesInstance = !instanceFilter
        ? true
        : run.bindingId === instanceFilter;
      const matchesStatus = !statusFilter || run.status === statusFilter;
      const matchesSearch =
        !term ||
        run.runName.toLowerCase().includes(term) ||
        (run.bindingName ?? '').toLowerCase().includes(term) ||
        (run.agentName ?? '').toLowerCase().includes(term) ||
        (run.agentCode ?? '').toLowerCase().includes(term) ||
        (run.ownerName ?? '').toLowerCase().includes(term) ||
        (run.ownerEmail ?? '').toLowerCase().includes(term);
      return matchesInstance && matchesStatus && matchesSearch;
    });
  }, [instanceFilter, runs, search, statusFilter]);

  const openRun = useCallback(
    (runId: string) => {
      navigate(`/ai-studio/runs/${encodeURIComponent(runId)}`);
    },
    [navigate]
  );

  const handleCreate = useCallback(
    async (request: CreateRunRequest) => {
      if (!request.instanceId) {
        setErrorKey('aiStudio.runs.createFailed');
        return;
      }
      setCreating(true);
      setErrorKey(null);
      setErrorDetail(null);
      try {
        const run = await createRun(request);
        openRun(run.id);
      } catch (e) {
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { error?: string } } }).response?.data?.error
            : null;
        setErrorKey('aiStudio.runs.createFailed');
        setErrorDetail(typeof msg === 'string' ? msg : null);
      } finally {
        setCreating(false);
      }
    },
    [openRun]
  );

  return (
    <main className="flex w-full flex-col gap-4 px-4 py-4 md:px-6 lg:px-8">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.runs.title')}
          </h1>
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.runs.subtitle')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm((current) => !current)}
          className="rounded-md bg-[var(--color-accent-bg)] px-4 py-2 text-base text-[var(--color-accent-text)] hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
        >
          {t('aiStudio.runs.startRun')}
        </button>
      </header>

      {errorKey && (
        <div
          role="alert"
          className="rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
        >
          {errorDetail ?? t(errorKey)}
        </div>
      )}

      {showForm && (
        <StartRunForm
          runnableInstances={runnableInstances}
          submitting={creating}
          onSubmit={(request) => void handleCreate(request)}
          onCancel={() => setShowForm(false)}
        />
      )}

      <section className="flex flex-wrap items-end gap-3">
        <div className="min-w-[16rem] flex-1">
          <label className={LABEL_CLASS} htmlFor="run-search">
            {t('aiStudio.runs.search')}
          </label>
          <input
            id="run-search"
            className={`${FIELD_CLASS} w-full`}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="run-instance-filter">
            {t('aiStudio.runs.form.instance')}
          </label>
          <select
            id="run-instance-filter"
            className={FIELD_CLASS}
            value={instanceFilter}
            onChange={(event) => setInstanceFilter(event.target.value)}
          >
            <option value="">{t('aiStudio.runs.filterAll')}</option>
            {instances.map((instance) => (
              <option key={instance.id} value={instance.id}>
                {instance.instanceName}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={LABEL_CLASS} htmlFor="run-status-filter">
            {t('aiStudio.runs.filterStatus')}
          </label>
          <select
            id="run-status-filter"
            className={FIELD_CLASS}
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value as StudioRunStatus | '')
            }
          >
            <option value="">{t('aiStudio.runs.filterAll')}</option>
            {STUDIO_RUN_STATUSES.map((status) => (
              <option key={status} value={status}>
                {t(`aiStudio.runStatus.${status}`)}
              </option>
            ))}
          </select>
        </div>
      </section>

      {loading ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.runs.loading')}
        </p>
      ) : visibleRuns.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.runs.empty')}
        </p>
      ) : (
        <RunTable runs={visibleRuns} onSelect={(runId) => openRun(runId)} />
      )}
    </main>
  );
};

export default AIStudioRunCatalogPage;
